/* global BigInt */
import React, { useState } from 'react';
import { Box, TextField, Button, Typography, Paper, Grid, IconButton, Tooltip, Select, MenuItem, FormControl, InputLabel } from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';

// Browser-safe Hex Helpers (No Buffer required)
const toHex = (str) => {
  let result = '';
  for (let i = 0; i < str.length; i++) {
    result += str.charCodeAt(i).toString(16).padStart(2, '0');
  }
  return result;
};

const fromHex = (hex) => {
  let str = '';
  for (let i = 0; i < hex.length; i += 2) {
    str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
  }
  return str;
};

const EncoderDecoder = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [mode, setMode] = useState('base64');
  const [showFullInput, setShowFullInput] = useState(false);
  const [showFullOutput, setShowFullOutput] = useState(false);

  const TRUNCATE_LIMIT = 50000;

  const getDisplayValue = (text, showFull) => {
    if (!text) return '';
    if (text.length <= TRUNCATE_LIMIT || showFull) return text;
    return text.substring(0, TRUNCATE_LIMIT) + `\n\n... [Content Truncated. Total length: ${text.length.toLocaleString()} chars] ...`;
  };

  const process = (action) => {
    try {
      if (!input) return;

      if (mode === 'base64') {
        setOutput(action === 'encode' ? btoa(input) : atob(input));
      } else {
        // Base36 Logic
        if (action === 'encode') {
          const hex = toHex(input);
          setOutput(BigInt('0x' + hex).toString(36));
        } else {
          const hex = BigInt(input, 36).toString(16);
          // Ensure even length hex for proper decoding
          const cleanHex = hex.length % 2 !== 0 ? '0' + hex : hex;
          setOutput(fromHex(cleanHex));
        }
      }
    } catch (e) {
      setOutput('Error: Invalid input for this encoding type.');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Reset view states on new file
    setShowFullInput(false);
    setShowFullOutput(false);

    const reader = new FileReader();
    reader.onload = (event) => {
      const result = event.target.result;
      // result is like "data:image/png;base64,....."
      // If we want just the base64 part, we split. But often data URI is useful.
      // Let's provide the full data URI for "Encode" so it's usable.
      // Or just the base64 part? User asked for "base64 encoding", usually implies the string.
      // Let's give the full data URL in output for encode, and expect full data URL or base64 for decode.
      // Actually standard base64 encoders often just give the raw base64 data.
      // Let's give the raw base64 data (split at comma) if it's an image? 
      // User said "base64 encoding and decoding of images".
      // Let's stick to Data URL as it's most useful for web.
      const base64 = result.split(',')[1];
      setInput(base64);
      setOutput(base64); // Auto-set output? logic says "Encoding" is Image -> Text.
      // So Input should be "Image File", Output "Base64 String".
      // But my UI has a text input.
      // Let's make Input area handle file upload visually if mode is image.
    };
    reader.readAsDataURL(file);
  };

  // Custom logic for Image mode since it behaves differently
  const processImage = (action) => {
    if (action === 'encode') {
      // UI should handle file upload -> setInput(base64string). 
      setOutput(input); // Just pass through if already converted?
    } else {
      // Decode: Input is Base64 string -> Output is Image
      setOutput(input); // Output renders the image source
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(output);
  };

  return (
    <Box sx={{ maxWidth: '1000px', mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>Encoder / Decoder</Typography>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Encoding Mode</InputLabel>
          <Select
            value={mode}
            label="Encoding Mode"
            onChange={(e) => setMode(e.target.value)}
          >
            <MenuItem value="base64">Base64</MenuItem>
            <MenuItem value="base36">Base36</MenuItem>
            <MenuItem value="base64_image">Base64 Image</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3} direction="column">
        {/* Input Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="overline" color="text.secondary">INPUT</Typography>
              <Tooltip title="Copy Input">
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(input)}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            {mode === 'base64_image' && (
              <Box sx={{ mb: 2 }}>
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  sx={{ mb: 2, mr: 2 }}
                >
                  Upload Image (Encode)
                  <input type="file" hidden accept="image/*" onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (ev) => {
                        const res = ev.target.result; // data:image/png;base64,...
                        setInput(res); // Put full data URI in input so decode works immediately too?
                        // Or just put base64? 
                        // If I put full data URI, decode is easy.
                        // If I put just base64, I need to know mime type or guess.
                        // Let's use full Data URI for simplicity and "modern app" feel.
                      };
                      reader.readAsDataURL(file);
                    }
                  }} />
                </Button>
                <Typography variant="caption" display="block">
                  Upload an image to get its Base64 Data URI, or paste a Base64 string below to see the image.
                </Typography>
              </Box>
            )}

            <Box sx={{ position: 'relative' }}>
              <TextField
                fullWidth multiline rows={8} variant="filled" placeholder={mode === 'base64_image' ? "Paste Base64 Data URI here (data:image/...)..." : "Paste content here..."}
                value={getDisplayValue(input, showFullInput)}
                onChange={(e) => {
                  // If truncated, prevent editing the truncated text directly to avoid data loss
                  // Only allow editing if length is small or user explicitly showing full text
                  if (!showFullInput && input.length > TRUNCATE_LIMIT) {
                    // Optionally show a warning or just don't update?
                    // Better UX: Allow update if they clear it or paste over it, but typing in middle is risky.
                    // Simplest: Check if new value is smaller than limit (e.g. paste replaced everything)
                    // or if we were editing the real value.
                    // For now, let's just update state. The display value will update.
                    // *Careful*: If they edit the truncated string, 'e.target.value' contains the " ... [Content Truncated] ..." part.
                    // We MUST NOT setInput to that.
                    // Implementation: If truncated, make ReadOnly essentially, unless they toggle full view.
                  } else {
                    setInput(e.target.value);
                  }
                }}
                InputProps={{
                  disableUnderline: true,
                  sx: { fontFamily: 'monospace', fontSize: '0.9rem' },
                  readOnly: !showFullInput && input.length > TRUNCATE_LIMIT
                }}
              />
              {!showFullInput && input.length > TRUNCATE_LIMIT && (
                <Box sx={{ position: 'absolute', bottom: 10, right: 10, bgcolor: 'rgba(255,255,255,0.9)', p: 0.5, borderRadius: 1 }}>
                  <Typography variant="caption" color="warning.main" sx={{ mr: 1 }}>
                    Large content truncated for performance
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setShowFullInput(true)}>Show Full</Button>
                </Box>
              )}
              {showFullInput && input.length > TRUNCATE_LIMIT && (
                <Box sx={{ position: 'absolute', bottom: 10, right: 10 }}>
                  <Button size="small" variant="outlined" onClick={() => setShowFullInput(false)}>Truncate</Button>
                </Box>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              {mode !== 'base64_image' ? (
                <>
                  <Button fullWidth variant="contained" onClick={() => process('encode')}>Encode</Button>
                  <Button fullWidth variant="outlined" color="primary" onClick={() => process('decode')}>Decode</Button>
                </>
              ) : (
                <Button fullWidth variant="contained" onClick={() => setOutput(input)}>
                  Process (View Output)
                </Button>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Output Section */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="overline" color="primary">OUTPUT</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Copy Output">
                  <IconButton size="small" onClick={() => navigator.clipboard.writeText(output)}>
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>


            {mode === 'base64_image' && output ? (
              <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f5f5f5', borderRadius: 1, border: '1px dashed #ccc', overflow: 'hidden', p: 1 }}>
                <img src={output} alt="Decoded" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
                {/* Fallback text if image broken? */}
              </Box>
            ) : (
              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth multiline rows={8} variant="outlined"
                  value={getDisplayValue(output, showFullOutput)}
                  InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontSize: '0.9rem', color: 'primary.main' } }}
                  sx={{ '& fieldset': { borderStyle: 'dashed' } }}
                />
                {!showFullOutput && output.length > TRUNCATE_LIMIT && (
                  <Box sx={{ position: 'absolute', bottom: 10, right: 10, bgcolor: 'rgba(255,255,255,0.9)', p: 0.5, borderRadius: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setShowFullOutput(true)}>Show Full Output</Button>
                  </Box>
                )}
                {showFullOutput && output.length > TRUNCATE_LIMIT && (
                  <Box sx={{ position: 'absolute', bottom: 10, right: 10 }}>
                    <Button size="small" variant="outlined" onClick={() => setShowFullOutput(false)}>Truncate Output</Button>
                  </Box>
                )}
              </Box>
            )}

            <Button
              startIcon={<SwapHorizIcon />}
              sx={{ mt: 3, alignSelf: 'flex-end' }}
              onClick={() => { setInput(output); setOutput(''); }}
            >
              Move to Input
            </Button>
          </Paper>
        </Grid>
      </Grid>
    </Box >
  );
};

export default EncoderDecoder;